import { Reveal } from "@/components/Reveal";
import { PageHero, Overline, SectionShell } from "@/components/primitives";
import FinalCTA from "@/sections/FinalCTA";
import {
  GLOBAL_PUBLICATIONS, NATIONAL_MEDIA, BROADCAST_MEDIA,
  FORUM_OPPORTUNITIES, NETWORK_INDUSTRIES, NETWORK_DISCLAIMER,
} from "@/data/content";

function NameGrid({ items, testId }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 border-l border-t hairline" data-testid={testId}>
      {items.map((name) => (
        <div
          key={name}
          className="group border-r border-b hairline min-h-[92px] md:min-h-[104px] flex items-center justify-center px-4 py-5 text-center bg-warm hover:bg-mist/60 transition-colors duration-300"
        >
          <span className="font-heading font-light text-navy/50 group-hover:text-navy transition-colors duration-300 text-base md:text-lg tracking-tight leading-snug">
            {name}
          </span>
        </div>
      ))}
    </div>
  );
}

function SectionHead({ n, label, title, className = "" }) {
  return (
    <div className={`max-w-3xl mb-12 ${className}`}>
      <Reveal><Overline>{`${n} — ${label}`}</Overline></Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-8 font-heading font-light text-navy tracking-tight text-3xl md:text-4xl lg:text-5xl leading-[1.05]">
          {title}
        </h2>
      </Reveal>
    </div>
  );
}

export default function Network() {
  return (
    <div data-testid="network-page">
      <PageHero
        label="Our Global Visibility Network"
        lines={["Connected to the World's", "Leading Visibility Ecosystems"]}
        intro="Visibility is built through relationships, credibility, and access to the right ecosystems."
      />

      {/* Lead-in */}
      <SectionShell className="py-16 md:py-24 border-b hairline">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 lg:col-start-1 space-y-6 max-w-3xl">
            <Reveal>
              <p className="text-lg md:text-xl leading-relaxed text-ink/75 font-body">
                HM Beacon continuously develops relationships across premium business media, national
                and international publications, executive forums, leadership communities, industry
                conferences, recognition platforms, and strategic partners.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-lg md:text-xl leading-relaxed text-ink/75 font-body">
                Our focus is to connect ambitious companies with meaningful visibility opportunities
                that build long-term brand authority and executive credibility.
              </p>
            </Reveal>
          </div>
        </div>
      </SectionShell>

      {/* Section 1 — Global Business Publications */}
      <SectionShell className="py-20 md:py-28 border-b hairline">
        <SectionHead n="01" label="Global Reach" title="Global Business Publications" />
        <Reveal delay={0.1}><NameGrid items={GLOBAL_PUBLICATIONS} testId="global-publications-grid" /></Reveal>
        <Reveal delay={0.15}>
          <p className="mt-8 font-heading font-light text-navy/70 text-xl md:text-2xl">
            …and access across <span className="text-navy">100+</span> global publications and editorial platforms.
          </p>
        </Reveal>
      </SectionShell>

      {/* Section 2 — National Media Network */}
      <section className="py-20 md:py-28 bg-mist/50 border-b hairline">
        <SectionShell>
          <SectionHead n="02" label="National Depth" title="National Media Network" />
          <Reveal delay={0.1}><NameGrid items={NATIONAL_MEDIA} testId="national-media-grid" /></Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 font-heading font-light text-navy/70 text-xl md:text-2xl max-w-4xl">
              …plus an extended network across <span className="text-navy">150+</span> national publications,
              digital media platforms, and industry-specific media.
            </p>
          </Reveal>
        </SectionShell>
      </section>

      {/* Section 3 — Broadcast & Executive Media */}
      <SectionShell className="py-20 md:py-28 border-b hairline">
        <SectionHead n="03" label="On Air" title="Broadcast & Executive Media" />
        <Reveal delay={0.1}><NameGrid items={BROADCAST_MEDIA} testId="broadcast-media-grid" /></Reveal>
        <Reveal delay={0.15}>
          <p className="mt-8 text-lg md:text-xl leading-relaxed text-ink/70 font-body max-w-3xl">
            Executive interviews, leadership conversations, business television, and premium
            storytelling opportunities.
          </p>
        </Reveal>
      </SectionShell>

      {/* Section 4 — Executive Forums & Leadership Communities */}
      <section className="py-20 md:py-28 bg-navy text-warm grain border-b border-white/10">
        <SectionShell>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <Reveal>
                <span className="overline inline-flex items-center gap-3 text-warm/60">
                  <span className="w-8 h-px bg-gold" />04 — Relationships
                </span>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-8 font-heading font-light tracking-tight text-3xl md:text-4xl lg:text-5xl leading-[1.05]">
                  Building Relationships Where Business Leaders Connect
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <Reveal delay={0.15}>
                <p className="text-lg leading-relaxed text-warm/70 font-body">
                  HM Beacon is actively building relationships across executive forums, invitation-only
                  leadership communities, global conferences, business summits, industry associations,
                  and strategic networking platforms.
                </p>
              </Reveal>
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
                {FORUM_OPPORTUNITIES.map((o, i) => (
                  <Reveal key={o} delay={0.05 * i}>
                    <div className="bg-navy px-6 py-6 h-full flex items-center gap-4">
                      <span className="font-heading text-gold text-xs tracking-widest">0{i + 1}</span>
                      <span className="font-heading font-light text-lg md:text-xl">{o}</span>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </SectionShell>
      </section>

      {/* Section 5 — Awards & Recognition */}
      <SectionShell className="py-20 md:py-28 border-b hairline">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4">
            <SectionHead n="05" label="Credibility" title="Awards & Recognition" className="mb-0" />
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            <Reveal>
              <p className="text-lg md:text-xl leading-relaxed text-ink/75 font-body max-w-2xl">
                HM Beacon also works across respected awards, leadership recognition platforms, business
                rankings, and executive honours as part of a broader visibility strategy — identifying
                opportunities that align with each client's growth journey.
              </p>
            </Reveal>
          </div>
        </div>
      </SectionShell>

      {/* Section 6 — Industries We Stay Connected With */}
      <section className="py-20 md:py-28 bg-mist/50 border-b hairline">
        <SectionShell>
          <div className="max-w-3xl mb-12">
            <Reveal><Overline>06 — Ecosystems</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-8 font-heading font-light text-navy tracking-tight text-3xl md:text-4xl lg:text-5xl leading-[1.05]">
                Industries We Stay Connected With
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 text-lg leading-relaxed text-ink/70 font-body">
                We actively engage with the publications, conferences, executive communities, and
                visibility ecosystems shaping these industries to identify opportunities that create
                long-term value for our clients.
              </p>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-l border-t hairline" data-testid="network-industries-grid">
            {NETWORK_INDUSTRIES.map((ind, i) => (
              <Reveal key={ind} delay={i * 0.03}>
                <div className="group border-r border-b hairline px-6 py-8 h-full bg-warm hover:bg-warm transition-colors duration-300">
                  <span className="font-heading text-gold text-xs tracking-widest">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-heading font-light text-navy text-xl md:text-2xl">{ind}</h3>
                </div>
              </Reveal>
            ))}
          </div>
        </SectionShell>
      </section>

      {/* Section 7 — Our Network Philosophy */}
      <SectionShell className="py-24 md:py-36 border-b hairline">
        <div className="max-w-4xl">
          <Reveal><Overline>07 — Philosophy</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-8 font-heading font-light text-navy tracking-tight text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
              Relationships Create Visibility
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-10 text-lg md:text-xl leading-relaxed text-ink/75 font-body max-w-2xl">
              At HM Beacon, we believe meaningful visibility is built through relationships — not
              transactions. Every month, we continue expanding our network across publishers, media
              platforms, executive communities, conference organizers, strategic partners, and industry
              leaders.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mt-6 font-heading font-light text-navy text-2xl md:text-3xl leading-snug max-w-2xl">
              Every new relationship strengthens the opportunities available to our clients.
            </p>
          </Reveal>
        </div>
      </SectionShell>

      {/* Disclaimer */}
      <SectionShell className="py-10">
        <p className="max-w-4xl text-xs leading-relaxed text-ink/45 font-body" data-testid="network-disclaimer">
          {NETWORK_DISCLAIMER}
        </p>
      </SectionShell>

      <FinalCTA />
    </div>
  );
}
